/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React from 'react';
import styles from './ContentBlock.module.scss';
import { ContentBlockWrapper } from './ContentBlockWrapper';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import NextImage from '@/components/NextImage';

const isURL = (targetPath: string): boolean => {
  return typeof targetPath === 'string' && targetPath.includes('://');
};

// Helper function to create descriptive alt text for social media icons
const getDescriptiveAltText = (altText: string | undefined): string => {
  if (!altText) {
    return '';
  }

  const platformName = altText.toLowerCase();

  const socialMediaDescriptions: Record<string, string> = {
    facebook: 'Visit AutoZone on Facebook',
    twitter: 'Visit AutoZone on Twitter',
    x: 'Visit AutoZone on X (formerly Twitter)',
    instagram: 'Visit AutoZone on Instagram',
    youtube: 'Visit AutoZone on YouTube',
    linkedin: 'Visit AutoZone on LinkedIn',
    tiktok: 'Visit AutoZone on TikTok',
    pinterest: 'Visit AutoZone on Pinterest',
  };

  return socialMediaDescriptions[platformName] || altText;
};

type Props = {
  label?: string;
  CTALink: string;
  Icon?: boolean;
  name: string;
  handleOpenModal?: () => void;
  index?: number;
  altText?: string;
  imageUrl?: string;
};

export function ContentBlock({
  CTALink,
  imageUrl,
  name,
  altText,
  handleOpenModal,
  label = '',
  Icon = false,
  index,
}: Props) {
  const locale = useLocale();
  const isUS = locale === countryCodes.us;
  const isSocialIcon = Icon && isURL(CTALink);

  const descriptiveAltText = Icon ? getDescriptiveAltText(altText) : altText;

  return (
    <ContentBlockWrapper
      CTALink={CTALink}
      label={label}
      Icon={Icon}
      handleOpenModal={handleOpenModal}
      name={name}
      index={index}
    >
      <div className={styles.footerTopItem} data-testid={`at_footer_icon_${name.split(' ')[0]}`}>
        <div className={isSocialIcon ? undefined : styles.footerTopItemLogo}>
          {imageUrl ? (
            <NextImage
              src={imageUrl}
              alt={Icon ? descriptiveAltText : ''}
              data-testid={`at_footer_icon_image_${name.split(' ')[0]}`}
              height={25}
              width={isSocialIcon ? 25 : 30}
              loading="lazy"
            />
          ) : null}
        </div>
        <div
          className={isUS ? styles.footerTopItemLabel : styles.mxFooterTopItemLabel}
          id={`at_footer_icon_button_${name.split(' ')[0]}`}
          data-testid={`at_footer_icon_button_${name.split(' ')[0]}`}
        >
          {label.toLowerCase()}
        </div>
      </div>
    </ContentBlockWrapper>
  );
}
